// Stock Watchlist — data saham IDX read-only dari backend/stockData.js (DB terpisah stock_data.db).
// UI text in English (dashboard language).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { api } from '../context/AuthContext';

const RANGES = [
  { key: '1M', days: 31 }, { key: '3M', days: 92 }, { key: '6M', days: 183 },
  { key: '1Y', days: 366 }, { key: '2Y', days: 731 }, { key: '5Y', days: 1827 },
];

const fmtNum = (v, d = 0) => (v == null ? '—' : Number(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }));
const fmtVol = (v) => {
  if (v == null) return '—';
  if (v >= 1e9) return (v / 1e9).toFixed(2) + 'B';
  if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M';
  if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
  return String(v);
};
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { timeZone: 'Asia/Jakarta', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const label = (s) => (s === '^JKSE' ? 'IHSG' : s.replace(/\.JK$/, ''));
const daysAgo = (n) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);

function ChangeCell({ value, pct }) {
  if (pct == null) return <span className="text-gray-400">—</span>;
  const up = pct > 0, down = pct < 0;
  const cls = up ? 'text-emerald-600' : down ? 'text-rose-600' : 'text-gray-500';
  return (
    <span className={`font-medium ${cls}`}>
      {up ? '▲' : down ? '▼' : '•'} {fmtNum(Math.abs(value), value != null && Math.abs(value) < 100 ? 2 : 0)} ({pct > 0 ? '+' : ''}{fmtNum(pct, 2)}%)
    </span>
  );
}

export default function StockWatchlist() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [range, setRange] = useState('6M');
  const [history, setHistory] = useState([]);
  const [newSym, setNewSym] = useState('');
  const [busy, setBusy] = useState('');
  const [lastLog, setLastLog] = useState(null);
  const [divs, setDivs] = useState([]);
  const [fund, setFund] = useState(null);

  const load = useCallback(() => {
    return Promise.all([api.get('/api/stocks/watchlist'), api.get('/api/stocks/log')])
      .then(([w, l]) => {
        setRows(w.data || []);
        setLastLog((l.data || [])[0] || null);
        setError('');
        setSelected((cur) => cur || (w.data && w.data[0] && w.data[0].symbol) || null);
      })
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!selected) return;
    const days = RANGES.find((r) => r.key === range)?.days || 183;
    api.get(`/api/stocks/history/${encodeURIComponent(selected)}?from=${daysAgo(days)}`)
      .then((r) => setHistory(r.data || []))
      .catch(() => setHistory([]));
  }, [selected, range]);

  useEffect(() => {
    if (!selected) return;
    api.get(`/api/stocks/dividends/${encodeURIComponent(selected)}`)
      .then((r) => setDivs(r.data || []))
      .catch(() => setDivs([]));
    api.get(`/api/stocks/fundamentals/${encodeURIComponent(selected)}`)
      .then((r) => setFund(r.data || null))
      .catch(() => setFund(null));
  }, [selected]);

  const addSymbol = (e) => {
    e.preventDefault();
    const s = newSym.trim();
    if (!s) return;
    setBusy('add');
    api.post('/api/stocks/watchlist', { symbol: s })
      .then((r) => { setNewSym(''); setSelected(r.data.symbol); return load(); })
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setBusy(''));
  };

  const removeSymbol = (sym) => {
    if (!window.confirm(`Remove ${label(sym)} from watchlist? Price history is kept.`)) return;
    api.delete(`/api/stocks/watchlist/${encodeURIComponent(sym)}`)
      .then(() => { if (selected === sym) setSelected(null); return load(); })
      .catch((err) => setError(err.response?.data?.error || err.message));
  };

  const refresh = (kind) => {
    setBusy(kind);
    api.post('/api/stocks/refresh', { kind })
      .then(() => load())
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setBusy(''));
  };

  const sel = rows.find((r) => r.symbol === selected);
  const stats = useMemo(() => {
    if (history.length < 2) return null;
    const first = history[0].close, last = history[history.length - 1].close;
    const closes = history.map((h) => h.close);
    return { pct: ((last - first) / first) * 100, hi: Math.max(...closes), lo: Math.min(...closes) };
  }, [history]);
  const lineColor = stats && stats.pct < 0 ? '#e11d48' : '#059669';

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">📈 Stock Watchlist</h1>
          <p className="text-sm text-gray-500">
            IDX prices from Yahoo Finance (free, delayed ~15 min). Auto-refresh every 10 min during market hours.
            {lastLog && <> Last fetch: {fmtTime(lastLog.run_at.replace(' ', 'T') + 'Z')} ({lastLog.ok} ok{lastLog.failed ? `, ${lastLog.failed} failed` : ''}).</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <form onSubmit={addSymbol} className="flex items-center gap-2">
            <input
              value={newSym}
              onChange={(e) => setNewSym(e.target.value.toUpperCase())}
              placeholder="Add ticker, e.g. ADRO"
              className="w-44 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            />
            <button disabled={!!busy} className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
              {busy === 'add' ? 'Adding…' : 'Add'}
            </button>
          </form>
          <button onClick={() => refresh('quotes')} disabled={!!busy} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50">
            {busy === 'quotes' ? 'Refreshing…' : 'Refresh prices'}
          </button>
          <button onClick={() => refresh('history')} disabled={!!busy} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50">
            {busy === 'history' ? 'Reloading…' : 'Reload history'}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-start justify-between rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-3 text-rose-500">✕</button>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Ticker</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-right">Last</th>
              <th className="px-3 py-2 text-right">Change</th>
              <th className="px-3 py-2 text-right">Day range</th>
              <th className="px-3 py-2 text-right">52W range</th>
              <th className="px-3 py-2 text-right">Volume</th>
              <th className="px-3 py-2 text-right">Market time</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-400">Loading…</td></tr>}
            {!loading && !rows.length && <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-400">Watchlist is empty. Add a ticker above.</td></tr>}
            {rows.map((r) => (
              <tr
                key={r.symbol}
                onClick={() => setSelected(r.symbol)}
                className={`cursor-pointer border-t border-gray-100 hover:bg-gray-50 ${selected === r.symbol ? 'bg-emerald-50/60' : ''}`}
              >
                <td className="px-3 py-2 font-semibold text-gray-800">{label(r.symbol)}</td>
                <td className="px-3 py-2 max-w-xs truncate text-gray-600" title={r.name || ''}>{r.name || '—'}</td>
                <td className="px-3 py-2 text-right font-semibold">{fmtNum(r.price, r.symbol === '^JKSE' ? 2 : 0)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap"><ChangeCell value={r.change} pct={r.change_pct} /></td>
                <td className="px-3 py-2 text-right text-gray-600 whitespace-nowrap">{fmtNum(r.day_low)} – {fmtNum(r.day_high)}</td>
                <td className="px-3 py-2 text-right text-gray-600 whitespace-nowrap">{fmtNum(r.week52_low)} – {fmtNum(r.week52_high)}</td>
                <td className="px-3 py-2 text-right text-gray-600">{fmtVol(r.volume)}</td>
                <td className="px-3 py-2 text-right text-xs text-gray-500 whitespace-nowrap">{fmtTime(r.market_time)}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); removeSymbol(r.symbol); }}
                    className="rounded px-2 py-1 text-xs text-gray-400 hover:bg-rose-50 hover:text-rose-600"
                    title="Remove from watchlist"
                  >✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                {label(selected)} <span className="text-sm font-normal text-gray-500">{sel?.name || ''}</span>
              </h2>
              {stats && (
                <p className="text-sm text-gray-500">
                  {range} change:{' '}
                  <span className={stats.pct >= 0 ? 'font-medium text-emerald-600' : 'font-medium text-rose-600'}>
                    {stats.pct >= 0 ? '+' : ''}{fmtNum(stats.pct, 2)}%
                  </span>
                  {' · '}High {fmtNum(stats.hi)} · Low {fmtNum(stats.lo)}
                </p>
              )}
            </div>
            <div className="flex gap-1">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setRange(r.key)}
                  className={`rounded-md px-3 py-1 text-xs font-medium ${range === r.key ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >{r.key}</button>
              ))}
            </div>
          </div>
          {history.length ? (
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <LineChart data={history} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={40} />
                  <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11 }} width={60} tickFormatter={(v) => fmtNum(v)} />
                  <Tooltip
                    formatter={(v, n) => [fmtNum(v, selected === '^JKSE' ? 2 : 0), n]}
                    labelFormatter={(l) => `Date: ${l}`}
                  />
                  <Line type="monotone" dataKey="close" name="Close" stroke={lineColor} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="py-10 text-center text-sm text-gray-400">No price history yet. Click “Reload history”.</p>
          )}
          {selected !== '^JKSE' && (
            <div className="mt-4 border-t border-gray-100 pt-3">
              <h3 className="mb-2 text-sm font-semibold text-gray-700">
                Fundamentals {fund?.updated_at && <span className="font-normal text-gray-400">· updated {fund.updated_at.slice(0, 10)} (weekly, Sunday 07:00)</span>}
              </h3>
              {fund ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                  {[
                    ['Market cap', fund.market_cap != null ? 'Rp ' + fmtVol(fund.market_cap) : null],
                    ['P/E (TTM)', fund.pe != null ? fmtNum(fund.pe, 2) + 'x' : null],
                    ['Forward P/E', fund.forward_pe != null ? fmtNum(fund.forward_pe, 2) + 'x' : null],
                    ['P/BV', fund.pbv != null ? fmtNum(fund.pbv, 2) + 'x' : null],
                    ['EPS (TTM)', fund.eps != null ? fmtNum(fund.eps, 2) : null],
                    ['Book value/sh', fund.book_value != null ? fmtNum(fund.book_value, 0) : null],
                    ['Div. yield', fund.div_yield != null ? fmtNum(fund.div_yield * 100, 2) + '%' : null],
                    ['ROE', fund.roe != null ? fmtNum(fund.roe * 100, 2) + '%' : null],
                    ['ROA', fund.roa != null ? fmtNum(fund.roa * 100, 2) + '%' : null],
                    ['Net margin', fund.profit_margin != null ? fmtNum(fund.profit_margin * 100, 2) + '%' : null],
                    ['Debt/Equity', fund.debt_to_equity != null ? fmtNum(fund.debt_to_equity, 1) + '%' : null],
                    ['Revenue (TTM)', fund.revenue != null ? 'Rp ' + fmtVol(fund.revenue) : null],
                    ['Revenue growth', fund.revenue_growth != null ? fmtNum(fund.revenue_growth * 100, 2) + '%' : null],
                    ['Earnings growth', fund.earnings_growth != null ? fmtNum(fund.earnings_growth * 100, 2) + '%' : null],
                  ].map(([k, v]) => (
                    <div key={k} className="rounded-lg bg-gray-50 px-3 py-2">
                      <div className="text-[11px] uppercase text-gray-400">{k}</div>
                      <div className="text-sm font-semibold text-gray-800">{v ?? '—'}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No fundamental data yet (fetched weekly on Sunday 07:00 WIB).</p>
              )}
            </div>
          )}
          {selected !== '^JKSE' && (
            <div className="mt-4 border-t border-gray-100 pt-3">
              <h3 className="mb-2 text-sm font-semibold text-gray-700">Dividends (last 5 years)</h3>
              {divs.length ? (
                <div className="flex flex-wrap gap-2">
                  {divs.map((d) => (
                    <span key={d.date} className="rounded-lg bg-emerald-50 px-3 py-1 text-xs text-emerald-800">
                      {d.date}: <b>Rp {fmtNum(d.amount, d.amount < 10 ? 2 : 0)}</b>
                      {sel?.price ? <span className="text-emerald-600"> ({fmtNum((d.amount / sel.price) * 100, 2)}%)</span> : null}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No dividend data yet (filled on the next history refresh).</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
